import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText, Thermometer, Heart, Scale, Ruler,
  Pill, DollarSign, Upload, Save, Plus, X, Loader, ArrowLeft
} from 'lucide-react';
import apiService from '../services/api';

const NewVisit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    visitDate: new Date().toISOString().split('T')[0],
    symptoms: '',
    vitals: {
      bloodPressure: '',
      temperature: '',
      weight: '',
      height: ''
    },
    diagnosis: '',
    treatment: '',
    prescriptions: '',
    notes: '',
    treatments: [{ id: 1, name: '', fee: '' }],
    followUpRequired: false
  });

  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    console.log('URL params:', { id });
    console.log('Full URL:', window.location.href);

    if (id) {
      loadPatient();
    } else {
      setError('No patient ID provided in URL');
      setLoading(false);
    }
  }, [id]);

  const loadPatient = async () => {
    try {
      setLoading(true);
      setError(null);
      const patientData = await apiService.getPatient(id);
      setPatient(patientData);
    } catch (err) {
      console.error('Failed to load patient:', err);
      setError('Failed to load patient data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleVitalsChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      vitals: {
        ...prev.vitals,
        [name]: value
      }
    }));
  };

  const handleTreatmentChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      treatments: prev.treatments.map(treatment =>
        treatment.id === id ? { ...treatment, [field]: value } : treatment
      )
    }));
  };

  const addTreatment = () => {
    const newId = Math.max(...formData.treatments.map(t => t.id)) + 1;
    setFormData(prev => ({
      ...prev,
      treatments: [...prev.treatments, { id: newId, name: '', fee: '' }]
    }));
  };

  const removeTreatment = (id) => {
    if (formData.treatments.length > 1) {
      setFormData(prev => ({
        ...prev,
        treatments: prev.treatments.filter(treatment => treatment.id !== id)
      }));
    }
  };

  const calculateTotalFee = () => {
    return formData.treatments.reduce((total, treatment) => {
      const fee = parseFloat(treatment.fee) || 0;
      return total + fee;
    }, 0);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(att => att.id !== id));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.symptoms.trim()) {
      newErrors.symptoms = 'Symptoms/reason for visit is required';
    }

    if (!formData.diagnosis.trim()) {
      newErrors.diagnosis = 'Diagnosis is required';
    }

    formData.treatments.forEach((treatment) => {
      if (treatment.name.trim() && treatment.fee && (isNaN(treatment.fee) || parseFloat(treatment.fee) < 0)) {
        newErrors[`treatment_${treatment.id}_fee`] = 'Please enter a valid fee amount';
      }
    });



    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateForm()) {
    return;
  }

  try {
    setSubmitting(true);
    setError(null);

    const visitData = {
      patientId: parseInt(id),
      date: formData.visitDate,
      symptoms: formData.symptoms,
      diagnosis: formData.diagnosis,
      treatment: formData.treatment || formData.treatments
        .filter(t => t.name.trim())
        .map(t => `${t.name}${t.fee ? ` (${t.fee} MMK)` : ''}`)
        .join(', '),
      prescriptions: formData.prescriptions,
      notes: formData.notes,
      // ← FIXED: Send treatments array instead of fee
      treatments: formData.treatments.filter(t => t.name.trim() && t.fee),
      followUpRequired: formData.followUpRequired ? 1 : 0,
      vitals: formData.vitals
    };

    console.log('Submitting visit data:', visitData);

    const newVisit = await apiService.createVisit(parseInt(id), visitData);
    console.log('Visit created:', newVisit);

    setSuccess(true);

    setTimeout(() => {
      navigate(`/patient/${id}`);
    }, 2000);

  } catch (err) {
    console.error('Failed to create visit:', err);
    setError(err.message || 'Failed to save visit record');
  } finally {
    setSubmitting(false);
  }
};

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-0">
        <div className="flex items-center justify-center py-12">
          <Loader className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading patient data...</span>
        </div>
      </div>
    );
  }

  if (error && !patient) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-0">
        <div className="card text-center py-8">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Patient</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-3">
            <button onClick={() => navigate('/search')} className="btn-primary">
              Back to Search
            </button>
            <button onClick={loadPatient} className="btn-secondary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-0">
        <div className="card bg-green-50 border-green-200 text-center py-8">
          <div className="text-green-600 mb-4">
            <Save className="h-12 w-12 mx-auto" />
          </div>
          <h2 className="text-xl font-bold text-green-800 mb-2">Visit Recorded Successfully!</h2>
          <p className="text-green-700">Total Fee: <strong>{calculateTotalFee().toFixed(2)} MMK</strong></p>
          <p className="text-green-600 mt-2">Redirecting to patient profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6 px-4 sm:px-0">
      {/* Header - Responsive */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-blue-600" />
              New Visit Record
            </h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              Patient: <strong>{patient?.full_name}</strong> (ID: {patient?.patient_id})
            </p>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-sm text-gray-600">Visit Date</p>
            <input
              type="date"
              name="visitDate"
              value={formData.visitDate}
              onChange={handleInputChange}
              className="form-input text-sm w-full sm:w-auto"
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card bg-red-50 border-red-200">
          <div className="flex items-center">
            <div className="text-red-600 mr-3">⚠️</div>
            <div>
              <p className="text-red-800 font-medium text-sm sm:text-base">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Symptoms & Reason */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Visit Details</h2>
          <div className="space-y-4">
            <div>
              <label className="form-label">Symptoms / Reason for Visit *</label>
              <textarea
                name="symptoms"
                value={formData.symptoms}
                onChange={handleInputChange}
                className={`form-input ${errors.symptoms ? 'border-red-500' : ''}`}
                placeholder="Describe the patient's symptoms or reason for visit..."
                rows="3"
                disabled={submitting}
              />
              {errors.symptoms && <p className="text-red-500 text-sm mt-1">{errors.symptoms}</p>}
            </div>
          </div>
        </div>

        {/* Vitals - Responsive Grid */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Heart className="h-5 w-5 mr-2 text-red-600" />
            Vital Signs
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="form-label">
                <Heart className="inline h-4 w-4 mr-1" />
                Blood Pressure
              </label>
              <input
                type="text"
                name="bloodPressure"
                value={formData.vitals.bloodPressure}
                onChange={handleVitalsChange}
                className="form-input"
                placeholder="120/80"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="form-label">
                <Thermometer className="inline h-4 w-4 mr-1" />
                Temperature (°F)
              </label>
              <input
                type="text"
                name="temperature"
                value={formData.vitals.temperature}
                onChange={handleVitalsChange}
                className="form-input"
                placeholder="98.6"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="form-label">
                <Scale className="inline h-4 w-4 mr-1" />
                Weight (lbs)
              </label>
              <input
                type="text"
                name="weight"
                value={formData.vitals.weight}
                onChange={handleVitalsChange}
                className="form-input"
                placeholder="150"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="form-label">
                <Ruler className="inline h-4 w-4 mr-1" />
                Height (ft/in)
              </label>
              <input
                type="text"
                name="height"
                value={formData.vitals.height}
                onChange={handleVitalsChange}
                className="form-input"
                placeholder="5'8&quot;"
                disabled={submitting}
              />
            </div>
          </div>
        </div>

        {/* Diagnosis & Treatment */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Diagnosis & Treatment</h2>

          <div className="space-y-4">
            <div>
              <label className="form-label">Doctor's Diagnosis *</label>
              <textarea
                name="diagnosis"
                value={formData.diagnosis}
                onChange={handleInputChange}
                className={`form-input ${errors.diagnosis ? 'border-red-500' : ''}`}
                placeholder="Enter the diagnosis..."
                rows="3"
                disabled={submitting}
              />
              {errors.diagnosis && <p className="text-red-500 text-sm mt-1">{errors.diagnosis}</p>}
            </div>

            <div>
              <label className="form-label">
                <Pill className="inline h-4 w-4 mr-1" />
                Prescriptions / Medications
              </label>
              <textarea
                name="prescriptions"
                value={formData.prescriptions}
                onChange={handleInputChange}
                className="form-input"
                placeholder="List prescribed medications, dosages, and instructions..."
                rows="3"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="form-label">Visit Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Additional notes about the visit..."
                rows="3"
                disabled={submitting}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="followUpRequired"
                name="followUpRequired"
                checked={formData.followUpRequired}
                onChange={handleInputChange}
                className="mr-2"
                disabled={submitting}
              />
              <label htmlFor="followUpRequired" className="text-sm text-gray-700">
                Follow-up appointment required
              </label>
            </div>
          </div>
        </div>

        {/* Treatment Fees - Mobile Optimized */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <DollarSign className="h-5 w-5 mr-2 text-green-600" />
            Treatment Fees
          </h2>

          <div className="space-y-4">
            {formData.treatments.map((treatment, index) => (
              <div key={treatment.id} className="space-y-3 sm:space-y-0 sm:flex sm:items-center sm:space-x-3">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Treatment name (e.g., extraction, cleaning)"
                    value={treatment.name}
                    onChange={(e) => handleTreatmentChange(treatment.id, 'name', e.target.value)}
                    className="form-input w-full"
                    disabled={submitting}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 sm:w-32">
                    <input
                      type="number"
                      placeholder="Fee (MMK)"
                      value={treatment.fee}
                      onChange={(e) => handleTreatmentChange(treatment.id, 'fee', e.target.value)}
                      className={`form-input w-full ${errors[`treatment_${treatment.id}_fee`] ? 'border-red-500' : ''}`}
                      min="0"
                      step="0.01"
                      disabled={submitting}
                    />
                    {errors[`treatment_${treatment.id}_fee`] && (
                      <p className="text-red-500 text-xs mt-1">{errors[`treatment_${treatment.id}_fee`]}</p>
                    )}
                  </div>
                  <div className="flex space-x-1">
                    {index === formData.treatments.length - 1 && (
                      <button
                        type="button"
                        onClick={addTreatment}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                        title="Add treatment"
                        disabled={submitting}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    )}
                    {formData.treatments.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTreatment(treatment.id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        title="Remove treatment"
                        disabled={submitting}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Total Fee Display */}
            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">Total Fee:</span>
                <span className="text-xl font-bold text-green-600">
                  {calculateTotalFee().toFixed(2)} MMK
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* File Attachments */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Upload className="h-5 w-5 mr-2 text-blue-600" />
            File Attachments
          </h2>

          <div className="space-y-4">
            <div>
              <label className="form-label">Upload Files (X-rays, Lab Results, etc.)</label>
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileUpload}
                className="form-input"
                disabled={submitting}
              />
              <p className="text-sm text-gray-600 mt-1">
                Supported formats: Images (JPG, PNG), PDF, Word documents
              </p>
            </div>

            {attachments.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Attached Files:</p>
                {attachments.map(file => (
                  <div key={file.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center min-w-0 flex-1">
                      <Upload className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate">{file.name}</span>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(file.id)}
                      className="text-red-600 hover:text-red-800 text-sm ml-2 flex-shrink-0"
                      disabled={submitting}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Submit Buttons - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 sm:space-x-4 pt-6">
          <button
            type="button"
            onClick={() => navigate(`/patient/${id}`)}
            className="btn-secondary flex items-center justify-center space-x-2 order-2 sm:order-1"
            disabled={submitting}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Cancel</span>
          </button>
          <button
            type="submit"
            className="btn-success flex items-center justify-center space-x-2 order-1 sm:order-2"
            disabled={submitting}
          >
            {submitting ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{submitting ? 'Saving...' : 'Save Visit Record'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewVisit;